import React, { Suspense, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import useHomeLayout from '../../../hooks/useHomeLayout';
import { WIDGET_REGISTRY, getWidgetById, isWidgetEligibleForProject } from '../widgetRegistry';
import { useProject } from '../../../config/ProjectContext';
import usePermission from '../../../authorization/permissionHook';
import useAnalytics from '../../../hooks/useAnalytics';
import useRegionFeatureGate from '../../../hooks/useRegionFeatureGate';
import { SHOW_MACHINE_EVENTS_PERMISSION, SHOW_CREDENTIALS_MANAGEMENT } from '../../../utils/constants';
import AddWidgetPlaceholder from './AddWidgetPlaceholder';

/**
 * Loading fallback for lazy-loaded widgets
 */
const WidgetLoadingFallback = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
    <div className="h-32 bg-gray-100 rounded"></div>
  </div>
);

/**
 * Widget Preview - renders a scaled-down non-interactive preview of a widget
 */
const WidgetPreview = ({ widget }) => {
  const WidgetComponent = widget.component;
  
  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
      {/* Preview container with scale transform */}
      <div 
        className="pointer-events-none select-none origin-top-left"
        style={{ 
          transform: 'scale(0.5)',
          width: '200%',
          height: '200%',
        }}
      >
        <Suspense fallback={<WidgetLoadingFallback />}>
          <WidgetComponent 
            isEditMode={false}
            isPreview={true}
          />
        </Suspense>
      </div>
      {/* Overlay to ensure no interactions leak through */}
      <div className="absolute inset-0" />
    </div>
  );
};

/**
 * Drop Zone between rows - always visible in edit mode
 */
const RowDropZone = ({ layoutRowId, insertIndex, onDrop, isEditMode, isLast = false }) => {
  const [isHovering, setIsHovering] = useState(false);

  if (!isEditMode) return null;

  return (
    <div
      className={`
        relative z-20 transition-all duration-150 ease-out
        ${isLast ? 'py-4' : 'py-2'}
        ${isHovering ? 'py-6' : ''}
      `}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setIsHovering(true);
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        setIsHovering(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsHovering(false);
        const widgetId = e.dataTransfer.getData('widgetId');
        if (widgetId) {
          onDrop(widgetId, layoutRowId, insertIndex);
        }
      }}
    >
      <div
        className={`
          w-full rounded-lg transition-all duration-150 ease-out
          border-2 border-dashed flex items-center justify-center
          ${isHovering 
            ? 'h-20 border-indigo-500 bg-indigo-100' 
            : isLast
              ? 'h-12 border-indigo-300 bg-indigo-50/50 hover:border-indigo-400 hover:bg-indigo-100/50'
              : 'h-8 border-gray-300 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/50'
          }
        `}
      >
        {isHovering ? (
          <span className="text-indigo-600 font-medium text-sm">
            Drag here to move widget
          </span>
        ) : (
          <span className={isLast ? 'text-indigo-400 text-sm' : 'text-gray-400 text-xs'}>
            {isLast ? 'Drop widget here' : 'Drag widget here'}
          </span>
        )}
      </div>
    </div>
  );
};

RowDropZone.propTypes = {
  layoutRowId: PropTypes.string.isRequired,
  insertIndex: PropTypes.number.isRequired,
  onDrop: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  isLast: PropTypes.bool,
};

/**
 * Available Widgets Modal
 * Shows visual previews of available widgets that can be added
 */
const AvailableWidgetsModal = ({ isOpen, onClose, widgets, onAddWidget }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div 
          role="presentation"
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 z-10 max-h-[90vh] overflow-y-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Add Widget
          </h3>

          {widgets.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              All available widgets are already on your home page.
            </p>
          ) : (
            <div className="space-y-4">
              {widgets.map((widget) => (
                <div 
                  key={widget.id}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-2 hover:border-indigo-300 transition-colors"
                >
                  {/* Widget Preview */}
                  <div className="mb-3 h-52 overflow-hidden rounded-lg">
                    <WidgetPreview widget={widget} />
                  </div>
                  
                  {/* Add Button */}
                  <button
                    onClick={() => {
                      onAddWidget(widget.id);
                      onClose();
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add {widget.name}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

WidgetPreview.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    component: PropTypes.elementType.isRequired,
  }).isRequired,
};

AvailableWidgetsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  widgets: PropTypes.array.isRequired,
  onAddWidget: PropTypes.func.isRequired,
};

/**
 * Home Container Component
 * Manages widget layout, edit mode, and personalization
 * 
 * Requirements: FR-009, FR-010, FR-014, FR-015, FR-016, FR-017
 */
const HomeContainer = ({ isEditMode }) => {
  const { project } = useProject();
  const { setAnalytics } = useAnalytics();
  const {
    layout,
    isLoading,
    hideWidget,
    showWidget,
    moveWidget,
    resetToDefault,
    hiddenWidgets,
  } = useHomeLayout();

  const [showAddModal, setShowAddModal] = useState(false);
  const [, setDraggingWidget] = useState(null);

  // Permission checks
  const [, hasDevicePermission] = usePermission(SHOW_MACHINE_EVENTS_PERMISSION);
  const [, hasCredentialsPermission] = usePermission(SHOW_CREDENTIALS_MANAGEMENT);
  const isFeatureAvailable = useRegionFeatureGate();

  /**
   * Check if a widget should be visible based on permissions and project eligibility
   */
  const isWidgetAvailable = useCallback((widgetId) => {
    const widget = getWidgetById(widgetId);
    if (!widget) return false;

    // Check project eligibility
    if (!isWidgetEligibleForProject(widget, project?.code)) {
      return false;
    }

    // Check data region eligibility (e.g. not available in China)
    if (widget.regionFeatureKey && !isFeatureAvailable(widget.regionFeatureKey)) {
      return false;
    }

    // Check permissions
    if (widget.permissionRequired) {
      if (widget.permissionRequired === SHOW_MACHINE_EVENTS_PERMISSION && !hasDevicePermission) {
        return false;
      }
      if (widget.permissionRequired === SHOW_CREDENTIALS_MANAGEMENT && !hasCredentialsPermission) {
        return false;
      }
    }

    return true;
  }, [project?.code, hasDevicePermission, hasCredentialsPermission, isFeatureAvailable]);

  /**
   * Get widgets that can be added (hidden + eligible)
   */
  const availableToAddWidgets = useMemo(() => {
    return WIDGET_REGISTRY.filter((widget) => {
      return hiddenWidgets.includes(widget.id) && isWidgetAvailable(widget.id);
    });
  }, [hiddenWidgets, isWidgetAvailable]);

  /**
   * Handle widget removal
   */
  const handleRemoveWidget = useCallback((widgetId) => {
    const success = hideWidget(widgetId);
    if (!success) {
      // Show message that at least one widget must remain
      alert('You must keep at least one widget visible.');
    } else {
      setAnalytics({
        event: 'HomePageLayoutUpdated',
        sectionId: 'home-container',
        extraParams: { updateType: 'remove', widgetId },
      });
    }
  }, [hideWidget, setAnalytics]);

  /**
   * Handle add widget from modal
   */
  const handleAddWidget = useCallback((widgetId) => {
    showWidget(widgetId);
    setAnalytics({
      event: 'HomePageLayoutUpdated',
      sectionId: 'home-container',
      extraParams: { updateType: 'add', widgetId },
    });
  }, [showWidget, setAnalytics]);

  /**
   * Handle reset to default
   */
  const handleResetToDefault = useCallback(() => {
    resetToDefault();
    setAnalytics({
      event: 'HomePageLayoutUpdated',
      sectionId: 'home-container',
      extraParams: { updateType: 'restore' },
    });
  }, [resetToDefault, setAnalytics]);

  // Get visible rows (rows with at least one visible widget)
  const visibleRows = useMemo(() => {
    return layout.rows
      .map((row, index) => ({
        ...row,
        originalIndex: index,
        visibleWidgets: row.widgets.filter(isWidgetAvailable),
      }))
      .filter((row) => row.visibleWidgets.length > 0);
  }, [layout.rows, isWidgetAvailable]);

  /**
   * Get flattened list of visible widget IDs in order
   */
  const flatWidgetList = useMemo(() => {
    return visibleRows.flatMap((row) => row.visibleWidgets);
  }, [visibleRows]);

  /**
   * Handle widget drop for reordering between visual rows
   */
  const handleRowDrop = useCallback((draggedWidgetId, layoutRowId, insertIndex) => {
    // Find current index of the dragged widget
    const currentIndex = flatWidgetList.indexOf(draggedWidgetId);
    
    // Adjust insert index if moving forward (because the widget will be removed first)
    let adjustedIndex = insertIndex;
    if (currentIndex !== -1 && currentIndex < insertIndex) {
      adjustedIndex = insertIndex - 1;
    }
    
    moveWidget(draggedWidgetId, layoutRowId, adjustedIndex);
    setDraggingWidget(null);
    setAnalytics({
      event: 'HomePageLayoutUpdated',
      sectionId: 'home-container',
      extraParams: { updateType: 'reorder', widgetId: draggedWidgetId },
    });
  }, [flatWidgetList, moveWidget, setAnalytics]);

  /**
   * Handle move widget up (swap with previous widget)
   */
  const handleMoveUp = useCallback((widgetId) => {
    const currentIndex = flatWidgetList.indexOf(widgetId);
    if (currentIndex <= 0) return; // Can't move up if already first
    
    // Move to the position of the previous widget
    const layoutRowId = visibleRows.length > 0 ? visibleRows[0].id : 'row-1';
    moveWidget(widgetId, layoutRowId, currentIndex - 1);
    setAnalytics({
      event: 'HomePageLayoutUpdated',
      sectionId: 'home-container',
      extraParams: { updateType: 'reorder', widgetId, direction: 'up' },
    });
  }, [flatWidgetList, visibleRows, moveWidget, setAnalytics]);

  /**
   * Handle move widget down (swap with next widget)
   */
  const handleMoveDown = useCallback((widgetId) => {
    const currentIndex = flatWidgetList.indexOf(widgetId);
    if (currentIndex < 0 || currentIndex >= flatWidgetList.length - 1) return; // Can't move down if already last
    
    // Move to position after the next widget
    const layoutRowId = visibleRows.length > 0 ? visibleRows[0].id : 'row-1';
    moveWidget(widgetId, layoutRowId, currentIndex + 2);
    setAnalytics({
      event: 'HomePageLayoutUpdated',
      sectionId: 'home-container',
      extraParams: { updateType: 'reorder', widgetId, direction: 'down' },
    });
  }, [flatWidgetList, visibleRows, moveWidget, setAnalytics]);

  /**
   * Render a widget by ID
   */
  const renderWidget = (widgetId) => {
    const widget = getWidgetById(widgetId);
    if (!widget || !isWidgetAvailable(widgetId)) {
      return null;
    }

    const WidgetComponent = widget.component;
    const currentIndex = flatWidgetList.indexOf(widgetId);
    const canMoveUp = currentIndex > 0;
    const canMoveDown = currentIndex >= 0 && currentIndex < flatWidgetList.length - 1;
    const canRemove = flatWidgetList.length > 1;

    return (
      <div 
        key={widgetId} 
        className={widget.colSpan || 'col-span-1'}
      >
        <Suspense fallback={<WidgetLoadingFallback />}>
          <WidgetComponent
            isEditMode={isEditMode}
            onRemove={handleRemoveWidget}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            canRemove={canRemove}
            onDragStart={() => setDraggingWidget(widgetId)}
            onDragEnd={() => setDraggingWidget(null)}
          />
        </Suspense>
      </div>
    );
  };

  // Calculate visual rows (group widgets into rows of max 3 columns)
  // Also track the starting widget index for drop positioning
  const visualRows = useMemo(() => {
    const rows = [];
    let currentRow = [];
    let currentColCount = 0;
    let widgetIndex = 0;
    let rowStartIndex = 0;

    // Get the first layout row that has widgets (for drop targeting)
    const firstLayoutRowWithWidgets = visibleRows.length > 0 ? visibleRows[0] : null;
    const layoutRowId = firstLayoutRowWithWidgets?.id || 'row-1';

    visibleRows.forEach((layoutRow) => {
      layoutRow.visibleWidgets.forEach((widgetId) => {
        const widget = getWidgetById(widgetId);
        const spanMatch = widget?.colSpan?.match(/col-span-(\d+)/);
        const span = spanMatch ? parseInt(spanMatch[1], 10) : 1;

        // If adding this widget would exceed 3 columns, start a new row
        if (currentColCount + span > 3 && currentRow.length > 0) {
          rows.push({ 
            widgets: currentRow, 
            colCount: currentColCount,
            startIndex: rowStartIndex,
            layoutRowId,
          });
          currentRow = [];
          currentColCount = 0;
          rowStartIndex = widgetIndex;
        }

        currentRow.push(widgetId);
        currentColCount += span;
        widgetIndex++;

        // If we've hit exactly 3 columns, also start a new row
        if (currentColCount >= 3) {
          rows.push({ 
            widgets: currentRow, 
            colCount: currentColCount,
            startIndex: rowStartIndex,
            layoutRowId,
          });
          currentRow = [];
          currentColCount = 0;
          rowStartIndex = widgetIndex;
        }
      });
    });

    // Don't forget the last row if it has widgets
    if (currentRow.length > 0) {
      rows.push({ 
        widgets: currentRow, 
        colCount: currentColCount,
        startIndex: rowStartIndex,
        layoutRowId,
      });
    }

    return rows;
  }, [visibleRows]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-48 bg-gray-100 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Edit Mode Controls */}
      {isEditMode && (
        <div className="flex items-center justify-end gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
          <button
            onClick={handleResetToDefault}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-600 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Reset Layout</span>
          </button>
        </div>
      )}

      {/* Widget Grid with Drop Zones */}
      <div className="space-y-2">
        {/* Drop zone at the top */}
        <RowDropZone
          layoutRowId={visualRows.length > 0 ? visualRows[0].layoutRowId : 'row-1'}
          insertIndex={0}
          onDrop={handleRowDrop}
          isEditMode={isEditMode}
        />

        {visualRows.map((visualRow, visualIndex) => {
          // All rows use 3-column grid with dense packing for placeholder positioning
          const gridClass = 'grid grid-cols-1 lg:grid-cols-3 gap-6 grid-flow-dense';

          // Check if this is the last visual row
          const isLastRow = visualIndex === visualRows.length - 1;
          
          // Show placeholder in the first row that has space (colCount < 3)
          // Only show once - in the first row with available space
          const hasSpace = visualRow.colCount < 3;
          const isFirstRowWithSpace = hasSpace && 
            visualRows.slice(0, visualIndex).every(row => row.colCount >= 3);
          const showPlaceholderHere = isEditMode && availableToAddWidgets.length > 0 && isFirstRowWithSpace;
          
          // Calculate the insert index for the drop zone after this row
          const nextRowStartIndex = isLastRow 
            ? visualRow.startIndex + visualRow.widgets.length
            : visualRows[visualIndex + 1].startIndex;

          return (
            <React.Fragment key={`visual-row-${visualIndex}`}>
              <div className={gridClass}>
                {visualRow.widgets.map((widgetId) => renderWidget(widgetId))}
                
                {/* Show Add Widget in first row that has space */}
                {showPlaceholderHere && (
                  <div className="col-span-1">
                    <AddWidgetPlaceholder
                      onClick={() => setShowAddModal(true)}
                    />
                  </div>
                )}
              </div>

              {/* Drop zone after each visual row */}
              <RowDropZone
                layoutRowId={visualRow.layoutRowId}
                insertIndex={nextRowStartIndex}
                onDrop={handleRowDrop}
                isEditMode={isEditMode}
                isLast={isLastRow && availableToAddWidgets.length === 0}
              />
            </React.Fragment>
          );
        })}

        {/* If all rows are full OR no visible rows, show placeholder in new row */}
        {isEditMode && availableToAddWidgets.length > 0 && 
          (visualRows.length === 0 || visualRows.every(row => row.colCount >= 3)) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1">
              <AddWidgetPlaceholder
                onClick={() => setShowAddModal(true)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Available Widgets Modal */}
      <AvailableWidgetsModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        widgets={availableToAddWidgets}
        onAddWidget={handleAddWidget}
      />
    </div>
  );
};

HomeContainer.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
};

export default HomeContainer;
